import React from 'react';
import { useStore, ClientAttribute } from '../../store/useStore';
import { Input, Select, Tabs, Repeater, Textarea } from '../ui';
import { UserCheck, BrainCircuit } from 'lucide-react';

export function M5_IdealClient() {
  const { idealClient, setIdealClient } = useStore();

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setIdealClient({ [e.target.name]: e.target.value });
  };

  const handleAddAttr = (type: 'dores' | 'sonhos' | 'objecoes' | 'medos') => {
    const novelItem: ClientAttribute = { id: crypto.randomUUID(), titulo: '', descricao: '' };
    setIdealClient({ [type]: [...idealClient[type], novelItem] });
  };

  const handleUpdateAttr = (type: 'dores' | 'sonhos' | 'objecoes' | 'medos', index: number, key: keyof ClientAttribute, value: string) => {
    const updated = [...idealClient[type]];
    updated[index] = { ...updated[index], [key]: value };
    setIdealClient({ [type]: updated });
  };

  const handleRemoveAttr = (type: 'dores' | 'sonhos' | 'objecoes' | 'medos', index: number) => {
    const updated = idealClient[type].filter((_, i) => i !== index);
    setIdealClient({ [type]: updated });
  };

  const tabs = [
    {
      id: 'demo',
      label: 'Perfil Demográfico',
      icon: <UserCheck size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
           <Select 
            label="Sexo" 
            name="sexo" 
            value={idealClient.sexo} 
            onChange={handleTextChange} 
            options={[
                {label: 'Selecionar...', value: ''},
                {label: 'Ambos', value: 'Ambos'},
                {label: 'Predominante Masculino', value: 'Predominante Masculino'},
                {label: 'Predominante Feminino', value: 'Predominante Feminino'},
            ]}
           />
           <Select 
            label="Faixa Etária" 
            name="faixaEtaria" 
            value={idealClient.faixaEtaria} 
            onChange={handleTextChange} 
            options={[
                {label: 'Selecionar...', value: ''},
                {label: '18-24 anos', value: '18-24'},
                {label: '25-34 anos', value: '25-34'},
                {label: '35-44 anos', value: '35-44'},
                {label: '45-54 anos', value: '45-54'},
                {label: '55+ anos', value: '55+'},
                {label: 'Todas as idades', value: 'Todas'},
            ]}
           />
           <Input label="Renda Média" name="renda" value={idealClient.renda} onChange={handleTextChange} placeholder="Ex: Classe A/B (Acima de 8k)" />
           <Input label="Cidade Foco" name="cidade" value={idealClient.cidade} onChange={handleTextChange} placeholder="Ex: São Paulo" />
           <Input label="Estado Foco" name="estado" value={idealClient.estado} onChange={handleTextChange} placeholder="Ex: SP" />
        </div>
      )
    },
    {
      id: 'comportamento',
      label: 'Mapeamento Comportamental',
      icon: <BrainCircuit size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Repeater<ClientAttribute>
                    title="Dores (Problemas)"
                    description="O que tira o sono do cliente?"
                    addButtonText="Nova Dor"
                    items={idealClient.dores}
                    onAdd={() => handleAddAttr('dores')}
                    onRemove={(index) => handleRemoveAttr('dores', index)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Nova Dor', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Qual a dor principal?" value={item.titulo} onChange={(e) => handleUpdateAttr('dores', index, 'titulo', e.target.value)} placeholder="Ex: Cabelo caindo" />
                        <Textarea label="Detalhe o contexto" value={item.descricao} onChange={(e) => handleUpdateAttr('dores', index, 'descricao', e.target.value)} placeholder="Como essa dor afeta a vida dele?" />
                      </div>
                    )}
                />
                
                <Repeater<ClientAttribute>
                    title="Sonhos (Transformação)"
                    description="O que ele quer alcançar com o produto?"
                    addButtonText="Novo Sonho"
                    items={idealClient.sonhos}
                    onAdd={() => handleAddAttr('sonhos')}
                    onRemove={(index) => handleRemoveAttr('sonhos', index)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Novo Sonho', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Qual o sonho/desejo?" value={item.titulo} onChange={(e) => handleUpdateAttr('sonhos', index, 'titulo', e.target.value)} placeholder="Ex: Recuperar autoestima" />
                        <Textarea label="Detalhe o impacto" value={item.descricao} onChange={(e) => handleUpdateAttr('sonhos', index, 'descricao', e.target.value)} placeholder="Como a vida dele seria diferente?" />
                      </div>
                    )}
                />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4 border-t border-gray-100">
                <Repeater<ClientAttribute>
                    title="Objeções (Barreiras de Compra)"
                    description="Por que ele NÃO compraria?"
                    addButtonText="Nova Objeção"
                    items={idealClient.objecoes}
                    onAdd={() => handleAddAttr('objecoes')}
                    onRemove={(index) => handleRemoveAttr('objecoes', index)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Nova Objeção', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Barreira principal" value={item.titulo} onChange={(e) => handleUpdateAttr('objecoes', index, 'titulo', e.target.value)} placeholder="Ex: Muito caro" />
                        <Textarea label="Como quebrar essa objeção?" value={item.descricao} onChange={(e) => handleUpdateAttr('objecoes', index, 'descricao', e.target.value)} placeholder="Argumento de vendas" />
                      </div>
                    )}
                />
                
                <Repeater<ClientAttribute>
                    title="Medos (Inseguranças)"
                    description="O que dá medo na hora da decisão?"
                    addButtonText="Novo Medo"
                    items={idealClient.medos}
                    onAdd={() => handleAddAttr('medos')}
                    onRemove={(index) => handleRemoveAttr('medos', index)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Novo Medo', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Qual o receio principal?" value={item.titulo} onChange={(e) => handleUpdateAttr('medos', index, 'titulo', e.target.value)} placeholder="Ex: Medo de ser enganado" />
                        <Textarea label="Detalhes" value={item.descricao} onChange={(e) => handleUpdateAttr('medos', index, 'descricao', e.target.value)} placeholder="Contexto..." />
                      </div>
                    )}
                />
            </div>
        </div>
      )
    }
  ];

  return <Tabs tabs={tabs} />;
}
